import { getCleverTapPageType } from "../fnpPageType";

const CLEAR_CART_POPUP_PRODUCT_SLOT_COUNT = 5;

/**
 * Builds CleverTap payload for Clear Cart Popup Viewed / Closed (up to 5 products + addon slots).
 *
 * @param {Object} params
 * @param {string} [params.page_name]
 * @param {string} [params.page_type]
 * @param {Array} [params.cartItems] - Cart order list (`giftItem` entries).
 * @param {string} [params.new_pincode]
 * @param {string} [params.old_pincode]
 * @param {string} [params.new_country]
 * @param {string} [params.old_country]
 * @returns {Object}
 */
export const buildClearCartPopupViewedData = ({
  page_name = "",
  page_type = "",
  cartItems = [],
  new_pincode = "",
  old_pincode = "",
  new_country = "",
  old_country = "",
} = {}) => {
  const orders = Array.isArray(cartItems) ? cartItems : [];
  let totalAddonCount = 0;

  const payload = {
    page_name: page_name || "",
    page_type: getCleverTapPageType(page_type),
    total_product_count: orders.length,
    new_pincode: new_pincode || "",
    old_pincode: old_pincode || "",
    new_country: new_country || "",
    old_country: old_country || "",
  };

  for (let index = 0; index < CLEAR_CART_POPUP_PRODUCT_SLOT_COUNT; index += 1) {
    const suffix = index + 1;
    const giftItem = orders[index]?.giftItem || orders[index];
    const mainProduct = giftItem?.mainproduct || giftItem;
    const addons = Array.isArray(giftItem?.addons) ? giftItem.addons : [];

    payload[`product_name_${suffix}`] = mainProduct?.productName || mainProduct?.title || "";
    payload[`pid_${suffix}`] = mainProduct?.productId || "";
    payload[`product_quantity_${suffix}`] = mainProduct?.quantity ?? mainProduct?.qty ?? "";

    if (addons.length) {
      const addonNames = [];
      let addonQuantitySum = 0;
      addons.forEach((addon) => {
        const addonProduct = addon?.mainproduct || addon;
        const addonName = addonProduct?.productName || addonProduct?.title || addon?.productName || addon?.title || "";
        if (addonName) {
          addonNames.push(addonName);
        }
        // Prefer explicit qty; default to 1 so addon_quantity_* is never stripped when addons exist.
        addonQuantitySum += Number(addonProduct?.quantity ?? addonProduct?.qty ?? addon?.quantity ?? addon?.qty) || 1;
      });
      payload[`addon_name_${suffix}`] = addonNames.join(", ");
      payload[`addon_quantity_${suffix}`] = addonQuantitySum;
    } else {
      payload[`addon_name_${suffix}`] = "";
      payload[`addon_quantity_${suffix}`] = "";
    }
  }

  orders.forEach((order) => {
    const giftItem = order?.giftItem || order;
    const addons = Array.isArray(giftItem?.addons) ? giftItem.addons : [];
    addons.forEach((addon) => {
      const addonProduct = addon?.mainproduct || addon;
      totalAddonCount += Number(addonProduct?.quantity ?? addonProduct?.qty ?? addon?.quantity ?? addon?.qty) || 1;
    });
  });

  payload.total_addon_count = totalAddonCount;
  return payload;
};

/**
 * Builds CleverTap payload for Explore Available Products Clicked.
 *
 * @param {Object} [params]
 * @param {string} [params.city]
 * @param {string} [params.pincode]
 * @param {string} [params.area]
 * @param {string} [params.country]
 * @param {number} [params.suborderCount]
 * @param {boolean} [params.isCartSplit] - When false (combined cart), suborder_count is 1.
 * @returns {Object}
 */
export const buildExploreAvailableProductsClickedData = ({
  city = "",
  pincode = "",
  area = "",
  country = "",
  suborderCount = 0,
  isCartSplit,
} = {}) => ({
  city: city || "",
  pincode: pincode || "",
  area: area || "",
  country: country || "",
  suborder_count: isCartSplit === false ? 1 : Number(suborderCount) || 0,
});
